import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AlertTriangle, Clock, X, CheckCircle, TrendingDown, TrendingUp, Award, Mail } from 'lucide-react';
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    // Set deadline to Friday January 30th 2026 at 11:59 PM ET
    const deadline = new Date('2026-01-30T23:59:59-05:00');
    const updateCountdown = () => {
      const now = new Date();
      const difference = deadline.getTime() - now.getTime();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference / (1000 * 60 * 60) % 24),
          minutes: Math.floor(difference / 1000 / 60 % 60),
          seconds: Math.floor(difference / 1000 % 60)
        });
      }
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  return <div className="flex justify-center gap-4 md:gap-8" data-id="element-387">
      {[{
      label: 'DAYS',
      value: timeLeft.days
    }, {
      label: 'HOURS',
      value: timeLeft.hours
    }, {
      label: 'MINUTES',
      value: timeLeft.minutes
    }, {
      label: 'SECONDS',
      value: timeLeft.seconds
    }].map((item, index) => <div key={item.label} className="flex flex-col items-center" data-id="element-388">
          <div className="relative" data-id="element-389">
            <div className="absolute inset-0 bg-red-500/30 blur-xl animate-pulse-slow" data-id="element-390" />
            <div className="relative bg-gradient-to-br from-red-950/80 to-black border-2 border-red-500 rounded-lg p-4 md:p-6 min-w-[80px] md:min-w-[100px]" data-id="element-391">
              <div className="text-4xl md:text-5xl font-bold text-red-400 text-center" style={{
            textShadow: '0 0 20px rgba(239, 68, 68, 0.6)'
          }} data-id="element-392">
                {String(item.value).padStart(2, '0')}
              </div>
            </div>
          </div>
          <div className="text-xs md:text-sm font-bold text-red-400 mt-2 tracking-wider" data-id="element-393">
            {item.label}
          </div>
        </div>)}
    </div>;
}
export function DeadlineAndPathsSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-20 px-6 bg-gradient-to-b from-black via-red-950/20 to-black overflow-hidden" data-id="element-394">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-395" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-396" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-397" />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto" data-id="element-398">
        {/* DEADLINE URGENCY SECTION */}
        <div className="relative mb-20" data-id="element-399">
          <div className="absolute inset-0 bg-gradient-radial from-red-600/30 via-transparent to-transparent blur-3xl" data-id="element-400" />

          <div className="relative bg-gradient-to-br from-red-950/80 via-stone-950/90 to-black border-4 border-red-600/80 p-10 md:p-12 rounded-lg shadow-[0_0_80px_rgba(239,68,68,0.6)]" data-id="element-401">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12" data-id="element-402">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-transparent" data-id="element-403" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-red-500 to-transparent" data-id="element-404" />
            </div>
            <div className="absolute top-0 right-0 w-12 h-12" data-id="element-405">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-red-500 to-transparent" data-id="element-406" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-red-500 to-transparent" data-id="element-407" />
            </div>
            <div className="absolute bottom-0 left-0 w-12 h-12" data-id="element-408">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-transparent" data-id="element-409" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-red-500 to-transparent" data-id="element-410" />
            </div>
            <div className="absolute bottom-0 right-0 w-12 h-12" data-id="element-411">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-red-500 to-transparent" data-id="element-412" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-red-500 to-transparent" data-id="element-413" />
            </div>

            {/* Warning Icon */}
            <div className="flex justify-center mb-8" data-id="element-414">
              <div className="relative" data-id="element-415">
                <div className="absolute inset-0 bg-red-500/50 blur-xl animate-pulse" data-id="element-416" />
                <AlertTriangle className="relative w-20 h-20 text-red-400 drop-shadow-[0_0_20px_rgba(239,68,68,0.9)]" data-id="element-417" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8" data-id="element-418">
              <h3 className="text-3xl md:text-5xl font-serif font-bold text-red-400 mb-6" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(239, 68, 68, 0.8)'
            }} data-id="element-419">
                ⚠️ THIS INVITATION CLOSES ON FRIDAY JANUARY 30th AT 11:59PM ET
                ⚠️
              </h3>
            </div>

            {/* Countdown Timer */}
            <div className="mb-12" data-id="element-420">
              <CountdownTimer data-id="element-421" />
            </div>

            {/* After that list */}
            <div className="space-y-4 text-lg" data-id="element-422">
              <p className="text-2xl font-bold text-red-400 text-center mb-6" data-id="element-423">
                After that:
              </p>

              <div className="space-y-4" data-id="element-424">
                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-425">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-426" />
                  <p className="text-stone-100" data-id="element-427">
                    The founding member offer disappears completely
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-428">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-429" />
                  <p className="text-stone-100" data-id="element-430">
                    Peptide Mastery 2.0 returns to $2,500 standalone
                    (pay-in-full only, no payment plans)
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-431">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-432" />
                  <p className="text-stone-100" data-id="element-433">
                    PSI PRO membership closes enrollment
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-434">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-435" />
                  <p className="text-stone-100" data-id="element-436">
                    You'll NEVER get founding member pricing OR be recognized as
                    one of the original Founding Members of this movement
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-437">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-438" />
                  <p className="text-stone-100" data-id="element-439">
                    You'll miss the opening strategic brief from Dr. Ali (titled
                    "Doing the "Impossible" In 2026 - How to Access Powers
                    Beyond the Mainstream")
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-440">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-441" />
                  <p className="text-stone-100" data-id="element-442">
                    You'll miss Brenden's opening intelligence drop (titled "The
                    Peptide Paradigm Shift: The Life-Changing Truth No One Has
                    Ever Told You")
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-443">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-444" />
                  <p className="text-stone-100" data-id="element-445">
                    You'll miss the live "inner circle" Q&A session where you'll
                    be able to questions and the masters themselves will give
                    you answers
                  </p>
                </div>

                <div className="flex items-start gap-3 bg-red-950/30 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-446">
                  <X className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" data-id="element-447" />
                  <p className="text-stone-100" data-id="element-448">
                    You'll miss the absolute best deal you will EVER see for
                    this package
                  </p>
                </div>
              </div>

              <div className="text-center mt-12" data-id="element-449">
                <p className="text-2xl md:text-3xl font-bold text-red-400" style={{
                textShadow: '0 0 30px rgba(239, 68, 68, 0.6)'
              }} data-id="element-450">
                  This exact offer, at this price, will never be available
                  again.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* WHAT HAPPENS IF YOU DO NOTHING SECTION */}
        <div className="relative mb-20" data-id="element-451">
          <div className="text-center mb-12" data-id="element-452">
            <h3 className="text-4xl md:text-5xl font-serif font-bold text-purple-400 mb-6" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
          }} data-id="element-453">
              What Happens If You Do Nothing?
            </h3>
            <p className="text-2xl text-stone-100" data-id="element-454">
              Here's what this decision is really about.
            </p>
            <p className="text-xl text-stone-300 mt-4" data-id="element-455">
              Six months from now, you'll be in one of two places:
            </p>
          </div>

          {/* Two Paths Comparison */}
          <div className="grid md:grid-cols-2 gap-8" data-id="element-456">
            {/* PATH 1: STUCK */}
            <div className="relative" data-id="element-457">
              <div className="absolute inset-0 bg-gradient-radial from-red-600/20 via-transparent to-transparent blur-3xl" data-id="element-458" />

              <div className="relative bg-gradient-to-br from-red-950/60 via-stone-950/80 to-black border-4 border-red-600/60 p-8 rounded-lg shadow-[0_0_60px_rgba(239,68,68,0.4)] h-full" data-id="element-459">
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8" data-id="element-460">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-transparent" data-id="element-461" />
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-red-500 to-transparent" data-id="element-462" />
                </div>
                <div className="absolute top-0 right-0 w-8 h-8" data-id="element-463">
                  <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-red-500 to-transparent" data-id="element-464" />
                  <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-red-500 to-transparent" data-id="element-465" />
                </div>

                {/* Icon */}
                <div className="flex justify-center mb-6" data-id="element-466">
                  <div className="relative" data-id="element-467">
                    <div className="absolute inset-0 bg-red-500/40 blur-xl" data-id="element-468" />
                    <TrendingDown className="relative w-12 h-12 text-red-400" data-id="element-469" />
                  </div>
                </div>

                {/* Title */}
                <h4 className="text-3xl font-bold text-red-400 text-center mb-6" style={{
                textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
              }} data-id="element-470">
                  PATH 1: STUCK IN UNCERTAINTY
                </h4>

                {/* List */}
                <div className="space-y-4 text-stone-300" data-id="element-471">
                  <p data-id="element-472">Still guessing which protocols are right.</p>
                  <p data-id="element-473">
                    Still piecing together conflicting information from YouTube
                    and Reddit.
                  </p>
                  <p data-id="element-474">
                    Still wondering if that chronic issue is "just normal" or
                    something you could fix.
                  </p>
                  <p data-id="element-475">
                    Still lying awake at night questioning whether you're doing
                    this right.
                  </p>
                  <p data-id="element-476">
                    Still watching your biology decay while the misinformation
                    tsunami gets worse.
                  </p>
                  <p data-id="element-477">
                    Still dealing with the same chronic issues you're dealing
                    with right now.
                  </p>
                  <p data-id="element-478">Still operating at the same energy level.</p>
                  <p data-id="element-479">Still experiencing the same brain fog.</p>
                  <p data-id="element-480">Still watching yourself slowly decline.</p>
                  <p className="text-red-400 font-bold text-lg mt-6" data-id="element-481">
                    Still exactly where you are today, just six months older and
                    further behind.
                  </p>
                </div>
              </div>
            </div>

            {/* PATH 2: TAKING CONTROL */}
            <div className="relative" data-id="element-482">
              <div className="absolute inset-0 bg-gradient-radial from-green-600/20 via-transparent to-transparent blur-3xl" data-id="element-483" />

              <div className="relative bg-gradient-to-br from-green-950/60 via-stone-950/80 to-black border-4 border-green-600/60 p-8 rounded-lg shadow-[0_0_60px_rgba(34,197,94,0.4)] h-full" data-id="element-484">
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8" data-id="element-485">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-transparent" data-id="element-486" />
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-500 to-transparent" data-id="element-487" />
                </div>
                <div className="absolute top-0 right-0 w-8 h-8" data-id="element-488">
                  <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-green-500 to-transparent" data-id="element-489" />
                  <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-green-500 to-transparent" data-id="element-490" />
                </div>

                {/* Icon */}
                <div className="flex justify-center mb-6" data-id="element-491">
                  <div className="relative" data-id="element-492">
                    <div className="absolute inset-0 bg-green-500/40 blur-xl" data-id="element-493" />
                    <TrendingUp className="relative w-12 h-12 text-green-400" data-id="element-494" />
                  </div>
                </div>

                {/* Title */}
                <h4 className="text-3xl font-bold text-green-400 text-center mb-6" style={{
                textShadow: '0 0 30px rgba(34, 197, 94, 0.5)'
              }} data-id="element-495">
                  PATH 2: TAKING CONTROL OF YOUR BIOLOGY
                </h4>

                {/* List */}
                <div className="space-y-4 text-stone-300" data-id="element-496">
                  <p data-id="element-497">
                    Implementing precision protocols designed by the world's
                    leading peptide scientist.
                  </p>
                  <p data-id="element-498">
                    Backed by 1,193 scientific citations and validated by
                    countless real-world transformations.
                  </p>
                  <p data-id="element-499">
                    Getting direct answers from masters who do this at some of
                    the highest levels in the world.
                  </p>
                  <p data-id="element-500">
                    Having your specific questions answered in live Q&A
                    sessions.
                  </p>
                  <p data-id="element-501">
                    Witnessing your biomarkers transform in ways that make your
                    doctor ask "what are you doing?"
                  </p>

                  <div className="bg-green-950/30 border-l-4 border-green-500 p-4 my-6" data-id="element-502">
                    <p className="text-xl font-bold text-green-400 mb-4" data-id="element-503">
                      Achieving results you didn't think were possible:
                    </p>
                    <div className="space-y-2 text-stone-100" data-id="element-504">
                      <p data-id="element-505">→ Chronic pain... ameliorated</p>
                      <p data-id="element-506">→ Brain fog... annihilated</p>
                      <p data-id="element-507">→ Energy levels… boosted</p>
                      <p data-id="element-508">→ Sleep quality... optimized</p>
                      <p data-id="element-509">→ Cognitive function… elevated</p>
                      <p data-id="element-510">→ Biological age... reversed</p>
                    </div>
                  </div>

                  <p data-id="element-511">Waking up with energy you haven't felt in years.</p>
                  <p data-id="element-512">Experiencing true mental clarity like never before.</p>
                  <p data-id="element-513">Moving without the pain you'd accepted as normal.</p>
                  <p data-id="element-514">
                    Operating at a biological level most people don't even know
                    exists.
                  </p>
                  <p data-id="element-515">
                    Becoming the person in your circle who people come to for
                    guidance.
                  </p>
                  <p data-id="element-516">
                    Having the confidence that you're doing everything correctly
                    because you've learned from the source.
                  </p>
                  <p data-id="element-517">
                    Belonging to an elite intelligence circle of biological
                    sovereigns who are 10-20 years ahead of mainstream medicine.
                  </p>
                  <p data-id="element-518">Having made the paradigm shift that changes everything.</p>
                  <p data-id="element-519">
                    Understanding what's ACTUALLY possible with human biology.
                  </p>
                  <p data-id="element-520">Operating with the Precision Cell™ methodology.</p>
                  <p className="text-green-400 font-bold text-lg mt-6" data-id="element-521">
                    Creating transformations for yourself and others that seem
                    "impossible" to everyone else.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Decision Point */}
          <div className="mt-12 text-center space-y-6" data-id="element-522">
            <div className="bg-gradient-to-br from-purple-950/60 via-stone-950/80 to-black border-2 border-purple-500/60 p-8 rounded-lg max-w-3xl mx-auto" data-id="element-523">
              <p className="text-2xl text-stone-100 mb-4" data-id="element-524">
                The difference between these two futures is the decision you
                make right now.
              </p>
              <p className="text-xl text-stone-300 mb-2" data-id="element-525">Not tomorrow.</p>
              <p className="text-xl text-stone-300 mb-2" data-id="element-526">
                Not "when you have more time to think about it."
              </p>
              <p className="text-3xl font-bold text-purple-400 mt-6" style={{
              textShadow: '0 0 30px rgba(168, 85, 247, 0.5)'
            }} data-id="element-527">
                Right now.
              </p>

              <div className="mt-8 space-y-4" data-id="element-528">
                <p className="text-xl text-stone-100" data-id="element-529">
                  Because the door is open.
                </p>
                <p className="text-xl font-bold text-amber-400" data-id="element-530">
                  But only for 4 more days.
                </p>
                <p className="text-xl text-stone-100" data-id="element-531">After that, it closes.</p>
                <p className="text-xl text-red-400 font-bold" data-id="element-532">
                  And this exact offer will never be available again.
                </p>
              </div>

              <div className="mt-8 space-y-4" data-id="element-533">
                <p className="text-xl text-stone-100" data-id="element-534">
                  You can spend the next six months guessing...
                </p>
                <p className="text-2xl font-bold text-green-400" data-id="element-535">
                  Or you can spend them optimizing with precision guidance from
                  the world's leading experts.
                </p>
              </div>

              <div className="mt-8 space-y-4" data-id="element-536">
                <p className="text-xl text-stone-100" data-id="element-537">The choice is yours.</p>
                <p className="text-xl font-bold text-red-400" data-id="element-538">
                  But the window to make that choice is closing fast.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FINAL PRICING SECTION */}
        <div className="relative" data-id="element-539">
          <div className="text-center mb-12" data-id="element-540">
            <h3 className="text-4xl md:text-5xl font-serif font-bold text-purple-400 mb-6" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
          }} data-id="element-541">
              Choose Your Path:
            </h3>
          </div>

          {/* OPTION 1: PAY IN FULL */}
          <div className="mb-12" data-id="element-542">
            <div className="relative bg-gradient-to-br from-green-950/40 to-black border-4 border-green-500/60 p-8 rounded-lg shadow-[0_0_40px_rgba(34,197,94,0.4)]" data-id="element-543">
              {/* Best Value Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-544">
                <div className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-500 rounded-full text-white text-sm font-bold shadow-lg" data-id="element-545">
                  BEST VALUE
                </div>
              </div>

              <div className="text-center mb-6 mt-4" data-id="element-546">
                <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500 p-8 my-6" data-id="element-547">
                  <p className="text-5xl font-bold text-amber-400 mb-2" style={{
                  textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
                }} data-id="element-548">
                    Only $2,200
                  </p>
                  <p className="text-xl text-stone-100" data-id="element-549">
                    for the complete package.
                  </p>
                </div>
              </div>

              <div className="space-y-4 text-lg text-stone-300" data-id="element-550">
                <div className="space-y-3 mb-6" data-id="element-551">
                  <p className="flex items-start gap-3" data-id="element-552">
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" data-id="element-553" />
                    <span className="text-stone-100" data-id="element-554">
                      Full Peptide Mastery 2.0 unlocked immediately
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-555">
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" data-id="element-556" />
                    <span className="text-stone-100" data-id="element-557">
                      12 months of PSI PRO membership
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-558">
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" data-id="element-559" />
                    <span className="text-stone-100" data-id="element-560">
                      All live sessions, bonuses, case studies, purity reports
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-561">
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" data-id="element-562" />
                    <span className="text-stone-100" data-id="element-563">
                      Then grandfathered in at this special rate for as long as
                      you're a member
                    </span>
                  </p>
                </div>

                <div className="bg-green-950/30 border-l-4 border-green-500 p-6 my-6" data-id="element-564">
                  <p className="text-xl font-bold text-green-400" data-id="element-565">
                    You're paying LESS than Peptide Mastery alone normally costs
                    ($2,500)...
                  </p>
                  <p className="text-xl font-bold text-green-400" data-id="element-566">
                    And getting 12 months of direct access to the masters
                    ($18,000+ value) included.
                  </p>
                </div>

                <a href="https://go.peptidescienceinstitute.com/pm-pro-annual-fm" className="block w-full px-6 py-5 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white text-center font-bold text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(34,197,94,0.4)] hover:shadow-[0_0_50px_rgba(34,197,94,0.6)] hover:scale-105" data-id="element-567">
                  SECURE YOUR FOUNDING MEMBER STATUS — PAY IN FULL
                </a>
              </div>
            </div>
          </div>

          {/* OPTION 2: MONTHLY PAYMENTS */}
          <div className="mb-12" data-id="element-568">
            <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/60 p-8 rounded-lg" data-id="element-569">
              <div className="text-center mb-6" data-id="element-570">
                <h4 className="text-3xl font-bold text-blue-400 mb-4" data-id="element-571">
                  OPTION 2
                </h4>
              </div>

              <div className="space-y-4 text-lg text-stone-300" data-id="element-572">
                <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500 p-8 my-6 text-center" data-id="element-573">
                  <p className="text-5xl font-bold text-amber-400 mb-2" style={{
                  textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
                }} data-id="element-574">
                    Only $220/mo
                  </p>
                  <p className="text-xl text-stone-100" data-id="element-575">for 12 months</p>
                </div>

                <div className="space-y-3 mb-6" data-id="element-576">
                  <p className="flex items-start gap-3" data-id="element-577">
                    <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" data-id="element-578" />
                    <span className="text-stone-100" data-id="element-579">
                      Peptide Mastery 2.0 unlocked monthly
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-580">
                    <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" data-id="element-581" />
                    <span className="text-stone-100" data-id="element-582">
                      12 months of PSI PRO membership
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-583">
                    <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" data-id="element-584" />
                    <span className="text-stone-100" data-id="element-585">
                      All live sessions, bonuses, case studies, purity reports
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-586">
                    <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" data-id="element-587" />
                    <span className="text-stone-100" data-id="element-588">
                      Then grandfathered at just $220/mo forever
                    </span>
                  </p>
                </div>

                <a href="https://go.peptidescienceinstitute.com/pm-pro-monthly-fm" className="block w-full px-6 py-5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-center font-bold text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:shadow-[0_0_50px_rgba(59,130,246,0.6)] hover:scale-105" data-id="element-589">
                  SECURE YOUR FOUNDING MEMBER STATUS — MONTHLY
                </a>
              </div>
            </div>
          </div>

          {/* OPTION 3: ALUMNI EXCLUSIVE */}
          <div data-id="element-590">
            <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/60 p-8 rounded-lg" data-id="element-591">
              <div className="text-center mb-6" data-id="element-592">
                <h4 className="text-3xl font-bold text-purple-400 mb-4" data-id="element-593">
                  ALUMNI EXCLUSIVE
                </h4>
              </div>

              <div className="space-y-4 text-lg text-stone-300" data-id="element-594">
                <p className="text-xl font-bold text-stone-100" data-id="element-595">
                  Already own Peptide Mastery?
                </p>

                <p className="text-stone-100" data-id="element-596">
                  You already have the blueprints.
                </p>

                <p className="text-xl font-bold text-purple-400" data-id="element-597">
                  Now join the active command center.
                </p>

                <div className="bg-purple-950/30 border-l-4 border-purple-500 p-6 my-6" data-id="element-598">
                  <p className="text-stone-100 mb-4" data-id="element-599">
                    Contact us at{' '}
                    <span className="font-bold text-purple-400" data-id="element-600">
                      support@support.peptidescienceinstitute.com
                    </span>{' '}
                    and let us know you're a Peptide Mastery owner.
                  </p>
                  <p className="text-stone-100" data-id="element-601">
                    We will send you the special "Peptide Mastery owner
                    discount" offer privately.
                  </p>
                </div>

                <a href="mailto:support@support.peptidescienceinstitute.com" className="flex items-center justify-center gap-3 w-full px-6 py-5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white text-center font-bold text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(168,85,247,0.4)] hover:shadow-[0_0_50px_rgba(168,85,247,0.6)] hover:scale-105" data-id="element-602">
                  <Mail className="w-6 h-6" data-id="element-603" />
                  CONTACT US FOR ALUMNI PRICING
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}